import type { ReportObligation } from '../api/client'
import './SeverityBars.css'

/**
 * One stacked bar per obligation, split by severity band.
 *
 * The form: the question is "how bad is each obligation, and which is worst",
 * which is composition across a handful of named categories — a stacked bar, read
 * left to right, with the bars sharing one scale so their lengths are comparable.
 *
 * The colour is a STATUS scale, not a categorical one: the bands mean
 * good → critical and their colours are reserved for that meaning. Status scales
 * are allowed to sit closer together than categorical series precisely because
 * they never travel without a label — which is why every visible segment here
 * carries its count, and the legend is always present. Expired-red and DueSoon-amber
 * are hard to separate for a deuteranope; the number inside each segment is what
 * makes the chart readable anyway, not the hue.
 *
 * Built as divs rather than SVG: it is a bar chart of six numbers per row, flexbox
 * already does the layout, and it stays selectable, printable and screen-readable
 * without a rendering library.
 */

/** Ordered worst-first, so the eye lands on the problem at the start of the bar. */
const BANDS = [
  { key: 'expiredCount', label: 'Expired', cls: 'crit' },
  { key: 'unknownCount', label: 'Unknown', cls: 'unk' },
  { key: 'dueSoonCount', label: 'Due soon', cls: 'warn' },
  { key: 'validCount', label: 'Valid', cls: 'ok' },
  { key: 'noExpiryCount', label: 'No expiry', cls: 'none' },
  { key: 'notApplicableCount', label: 'Not applicable', cls: 'na' },
] as const

export function SeverityBars({ obligations }: { obligations: ReportObligation[] }) {
  // One shared scale across every bar. Scaling each bar to its own total would
  // make a row of 3 vehicles look identical to a row of 300.
  const max = Math.max(
    1,
    ...obligations.map((o) => BANDS.reduce((sum, b) => sum + (o[b.key] ?? 0), 0)),
  )

  return (
    <figure className="sevbars">
      <figcaption className="sevbars__cap">
        Obligations by state
        <span className="sevbars__sub">
          Each bar is one obligation across every vehicle in this report.
        </span>
      </figcaption>

      {/* Always present: identity must never rest on colour alone, and on a
          printed page colour may not survive at all. */}
      <ul className="sevbars__legend">
        {BANDS.map((b) => (
          <li key={b.key}>
            <i className={`sevbars__key sevbars__key--${b.cls}`} aria-hidden="true" />
            {b.label}
          </li>
        ))}
      </ul>

      <div className="sevbars__rows">
        {obligations.map((o) => {
          const total = BANDS.reduce((sum, b) => sum + (o[b.key] ?? 0), 0)

          return (
            <div key={o.complianceTypeId} className="sevbars__row">
              <span className="sevbars__name">{o.displayName}</span>

              <div className="sevbars__track" style={{ width: `${(total / max) * 100}%` }}>
                {BANDS.map((b) => {
                  const value = o[b.key] ?? 0
                  if (value === 0) return null

                  const share = (value / total) * 100

                  return (
                    <span
                      key={b.key}
                      className={`sevbars__seg sevbars__seg--${b.cls}`}
                      style={{ width: `${share}%` }}
                      title={`${o.displayName} — ${b.label}: ${value}`}
                    >
                      {/*
                        Labelled only where the number genuinely fits. A label that
                        would be clipped is worse than none — the count is in the
                        tooltip and in the table below either way, so nothing is
                        gated behind the chart.
                      */}
                      {share >= 11 && <span className="sevbars__val">{value}</span>}
                    </span>
                  )
                })}
              </div>

              <span className="sevbars__total">{total}</span>
            </div>
          )
        })}
      </div>
    </figure>
  )
}
